import { useAuthStore } from '@/Utility/auth/Auth';
import type { Frags_With_Author_Fragment$data, Frags_Wiz_Author_Fragment$data } from '@/graphql/queries/Frags.query';
import type { User_Fragment$data } from '@/graphql/queries/User.query';
import type { Frags_Status_Settings_Fragment$data } from '@/graphql/queries/__generated__/Frags_Status_Settings_Fragment.graphql';
import {
  type Contact,
  type CrmList,
  type Form,
  type FormEntry,
  type Hub,
  type HubPage,
  PostStatusEnum,
  type StatusSettings,
  type StatusSettingsInput,
  StatusUiPermissionEnum,
  type User,
  type Znglist,
  type ZnglyInfo,
  type ZnglyVideo,
} from '@/graphql/types';
import type { FragmentData } from '@/graphql/utils';
import {
  CheckCircle,
  Delete,
  Groups,
  LockPerson,
  MarkEmailRead,
  PendingActions,
  Person,
  PersonSearch,
  VisibilityOff,
  VpnKey,
} from '@mui/icons-material';
import { useMemo } from 'react';
import type { DeepNullable, DeepPartial, ValueOf } from 'ts-essentials';

const nodeTypes = Object.freeze({
  catalog: {
    node: null as unknown as Partial<ZnglyVideo>,
    prettyName: 'Catalog',
    capSingleKey: 'content',
    capPluralKey: 'contents',
  },
  znglist: {
    node: null as unknown as Partial<Znglist>,
    prettyName: 'Znglist',
    capSingleKey: 'zngly_list',
    capPluralKey: 'zngly_lists',
  },
  hub: {
    node: null as unknown as Partial<Hub>,
    prettyName: 'Hub',
    capSingleKey: 'hub',
    capPluralKey: 'hubs',
  },
  hubPage: {
    node: null as unknown as Partial<HubPage>,
    prettyName: 'Hub Page',
    capSingleKey: 'hub_page',
    capPluralKey: 'hub_pages',
  },
  form: {
    node: null as unknown as Partial<Form>,
    prettyName: 'Form',
    capSingleKey: 'form',
    capPluralKey: 'forms',
  },
  formEntry: {
    node: null as unknown as Partial<FormEntry>,
    prettyName: 'Form Entry',
    capSingleKey: 'form_entry',
    capPluralKey: 'form_entries',
  },
  contact: {
    node: null as unknown as Partial<Contact>,
    prettyName: 'Contact',
    capSingleKey: 'contact',
    capPluralKey: 'contacts',
  },
  znglyInfo: {
    node: null as unknown as Partial<ZnglyInfo>,
    prettyName: 'Info',
    capSingleKey: 'zngly_info',
    capPluralKey: 'zngly_infos',
  },
  crmList: {
    node: null as unknown as Partial<CrmList>,
    prettyName: 'CRM List',
    capSingleKey: 'crm_list',
    capPluralKey: 'crm_lists',
  },
} as const);

type NodeTypes = typeof nodeTypes;

export type { NodeTypes as NodeTypesForStatus };

type AllowedPostStatusEnums =
  | keyof (typeof STATUS_GROUPS)['addAs']
  | keyof (typeof STATUS_GROUPS)['visibility']
  | PostStatusEnum;

type GetStatusOptions = {
  /**
   * Force include statuses
   */
  include?: AllowedPostStatusEnums[];
  /**
   * Force exclude statuses
   */
  exclude?: AllowedPostStatusEnums[];
  // /**
  //  * Node for which to get the status -> this allows us to better filter based on user permissions
  //  */
  // node?: NodeTypes[T]['node'];
  /**
   * Author of the node
   */
  author?:
    | User
    | FragmentData<User_Fragment$data>
    | Frags_With_Author_Fragment$data['node']
    | Frags_Wiz_Author_Fragment$data['node']
    | null;
};

type StatusObject = {
  label: string;
  value: AllowedPostStatusEnums;
  icon: React.JSX.Element;
  tooltip: <T extends keyof NodeTypes>(type: T) => string;
};

const STATUS_VALUES = Object.freeze({
  // add as
  [PostStatusEnum.Draft]: {
    label: 'Draft',
    value: PostStatusEnum.Draft,
    icon: <VisibilityOff />,
    tooltip: (type: keyof NodeTypes) => {
      return `While in draft, no one other than you can see this ${nodeTypes[type].prettyName}`;
    },
  },
  [PostStatusEnum.Pending]: {
    label: 'Pending Review',
    value: PostStatusEnum.Pending,
    icon: <PendingActions />,
    tooltip: (type: keyof NodeTypes) => {
      return `While in pending review, only you and users with the capability to review can see this ${nodeTypes[type].prettyName}`;
    },
  },
  add_as_approved: {
    label: 'Approved',
    value: 'add_as_approved',
    icon: <CheckCircle />,
    tooltip: (type: keyof NodeTypes) => {
      return `Once approved, this ${nodeTypes[type].prettyName} will use the visibility settings`;
    },
  },

  // visibility
  [PostStatusEnum.Publish]: {
    label: 'Published',
    value: PostStatusEnum.Publish,
    icon: <Groups />,
    tooltip: (type: keyof NodeTypes) => {
      if (type === 'form') {
        return `This ${nodeTypes[type].prettyName} is available for use`;
      }

      return `This ${nodeTypes[type].prettyName} is visible to everyone (Front Page, Categories, Search, etc...)`;
    },
  },
  [PostStatusEnum.HiddenBS]: {
    label: 'Published When Pinned',
    value: PostStatusEnum.HiddenBS,
    icon: <PersonSearch />,
    // tooltip: 'This content is only visible when pinned to a specific location',
    tooltip: (type: keyof NodeTypes) => {
      return `This ${nodeTypes[type].prettyName} is only visible when pinned to a specific location`;
    },
  },
  [PostStatusEnum.Hidden]: {
    label: 'Private',
    value: PostStatusEnum.Hidden,
    icon: <Person />,
    tooltip: (type: keyof NodeTypes) => {
      return `This ${nodeTypes[type].prettyName} is only visible to logged in users and can be accessed using a direct link`;
    },
  },
  private_restricted: {
    label: 'Private (Restricted)',
    value: 'private_restricted',
    icon: <LockPerson />,
    tooltip: (type: keyof NodeTypes) => {
      return `This ${nodeTypes[type].prettyName} is only visible to logged in users and can be accessed using a direct link. Other users can view or use this ${nodeTypes[type].prettyName} in their workflow.`;
    },
  },

  // extra
  [PostStatusEnum.Trash]: {
    label: 'Trash',
    value: PostStatusEnum.Trash,
    icon: <Delete />,
    tooltip: (type: keyof NodeTypes) => {
      return `This ${nodeTypes[type].prettyName} is in trash.`;
    },
  },
} as const);

const STATUS_UI_PERMISSIONS = Object.freeze({
  [StatusUiPermissionEnum.Public]: {
    label: 'Public',
    value: StatusUiPermissionEnum.Public,
    icon: undefined,
  },
  [StatusUiPermissionEnum.Restricted]: {
    label: 'Restricted',
    value: StatusUiPermissionEnum.Restricted,
    icon: undefined,
  },
  [StatusUiPermissionEnum.Token]: {
    label: 'Token',
    value: StatusUiPermissionEnum.Token,
    icon: <VpnKey />,
  },
  [StatusUiPermissionEnum.Otp]: {
    label: 'OTP',
    value: StatusUiPermissionEnum.Otp,
    icon: <MarkEmailRead />, // Using MarkEmailRead as a placeholder for OTP
  },
} as const);

const STATUS_GROUPS = Object.freeze({
  addAs: {
    add_as_approved: STATUS_VALUES.add_as_approved,
    [PostStatusEnum.Pending]: STATUS_VALUES[PostStatusEnum.Pending],
    [PostStatusEnum.Draft]: STATUS_VALUES[PostStatusEnum.Draft],
  },
  visibility: {
    [PostStatusEnum.Publish]: STATUS_VALUES[PostStatusEnum.Publish],
    [PostStatusEnum.Hidden]: STATUS_VALUES[PostStatusEnum.Hidden],
    [PostStatusEnum.HiddenBS]: STATUS_VALUES[PostStatusEnum.HiddenBS],
    private_restricted: STATUS_VALUES.private_restricted,
  },
} as const);

type AddAsType = {
  value: keyof (typeof STATUS_GROUPS)['addAs'];
} & Omit<StatusObject, 'value'>;

type VisibilityType = {
  value: keyof (typeof STATUS_GROUPS)['visibility'];
} & Omit<StatusObject, 'value'>;

type StatusUiPermissionType = {
  value: StatusUiPermissionEnum;
} & Omit<ValueOf<typeof STATUS_UI_PERMISSIONS>, 'value'>;

export type StatusGroups = {
  addAs: AddAsType;
  visibility?: VisibilityType;
  uiPermission?: StatusUiPermissionType;
};

/**
 * Convert the status settings to the dropdown groups
 */
export const getStatusGroupsFromSettings = (opts: StatusSettings): StatusGroups | null => {
  if (!opts?.status) {
    return null;
  }

  const { status, uiPermission, stateWhenApproved } = opts;

  if (status === PostStatusEnum.Draft || status === PostStatusEnum.Pending) {
    return {
      addAs: STATUS_GROUPS.addAs[status],
      visibility: !!stateWhenApproved
        ? getStatusGroupsFromSettings({
            status: stateWhenApproved.status,
            uiPermission: stateWhenApproved.uiPermission,
          })?.visibility
        : STATUS_GROUPS.visibility[PostStatusEnum.Hidden],
      uiPermission:
        STATUS_UI_PERMISSIONS[
          stateWhenApproved?.uiPermission?.type || uiPermission?.type || StatusUiPermissionEnum.Public
        ],
    };
  }

  if ([PostStatusEnum.Publish, PostStatusEnum.Hidden, PostStatusEnum.HiddenBS].includes(status)) {
    if (status === PostStatusEnum.Publish) {
      return {
        addAs: STATUS_GROUPS.addAs.add_as_approved,
        visibility: STATUS_GROUPS.visibility[PostStatusEnum.Publish],
        uiPermission: STATUS_UI_PERMISSIONS[uiPermission?.type || StatusUiPermissionEnum.Public],
      };
    }

    if (status === PostStatusEnum.HiddenBS || status === PostStatusEnum.Hidden) {
      const isPrivateRestricted =
        status === PostStatusEnum.Hidden && uiPermission?.type === StatusUiPermissionEnum.Restricted;

      return {
        addAs: STATUS_GROUPS.addAs.add_as_approved,
        visibility: isPrivateRestricted
          ? STATUS_GROUPS.visibility.private_restricted
          : STATUS_GROUPS.visibility[status],
        uiPermission: STATUS_UI_PERMISSIONS[uiPermission?.type || StatusUiPermissionEnum.Public],
      };
    }
  }

  return null;
};

/**
 * Convert the dropdown group values to status settings type
 */
export const getStatusSettingsFromGroups = (
  addAs: AddAsType['value'],
  visibility: VisibilityType['value'],
  uiPermission: StatusUiPermissionEnum = StatusUiPermissionEnum.Public
): StatusSettings | null => {
  const isPrivateRestricted = visibility === 'private_restricted';
  let finalUiPermission: StatusUiPermissionEnum = uiPermission;

  if (visibility === 'private_restricted' && uiPermission !== StatusUiPermissionEnum.Restricted) {
    finalUiPermission = StatusUiPermissionEnum.Restricted;
  }
  if (visibility !== 'private_restricted' && uiPermission === StatusUiPermissionEnum.Restricted) {
    finalUiPermission = StatusUiPermissionEnum.Public;
  }

  if (addAs === PostStatusEnum.Draft || addAs === PostStatusEnum.Pending) {
    return {
      status: addAs,
      uiPermission: {
        type: finalUiPermission,
      },
      stateWhenApproved: {
        status: isPrivateRestricted ? PostStatusEnum.Hidden : visibility,
        uiPermission: {
          type: finalUiPermission,
        },
      },
    };
  }

  if (addAs === 'add_as_approved') {
    if (visibility === PostStatusEnum.Publish) {
      return {
        status: PostStatusEnum.Publish,
        uiPermission: {
          type: finalUiPermission,
        },
        stateWhenApproved: null,
      };
    }

    if (visibility === PostStatusEnum.HiddenBS) {
      return {
        status: PostStatusEnum.HiddenBS,
        uiPermission: {
          type: finalUiPermission,
        },
        stateWhenApproved: null,
      };
    }

    if (visibility === PostStatusEnum.Hidden || visibility === 'private_restricted') {
      return {
        status: PostStatusEnum.Hidden,
        uiPermission: {
          type: finalUiPermission,
        },
        stateWhenApproved: null,
      };
    }
  }

  return null;
};

export { STATUS_GROUPS };

type GenericStatusSettings = DeepNullable<DeepPartial<Frags_Status_Settings_Fragment$data>> | StatusSettings;

export const formatStatusSettingsForInput = (data: GenericStatusSettings | null | undefined): StatusSettingsInput => {
  if (!data) {
    return {
      status: PostStatusEnum.Draft,
      uiPermission: {
        type: StatusUiPermissionEnum.Public,
      },
      stateWhenApproved: null,
    };
  }

  return {
    ...(data as StatusSettingsInput),
    ...(data?.approved && {
      approved: {
        id: data.approved?.id,
      },
    }),
  };
};

/**
 * Based on the post type, user, and options (current post author)
 * We want to filter the allowed statuses that the user can set
 */
const getAllowedStatusArray = <T extends keyof NodeTypes>(
  type: T,
  user: ReturnType<(typeof useAuthStore)['getState']>['user'],
  opts?: GetStatusOptions
): StatusObject[] => {
  const initValues = Object.values(STATUS_VALUES).filter(
    (v) => v.value !== 'add_as_approved' && v.value !== 'private_restricted'
  ) as StatusObject[];
  let values = [...initValues];

  const permissions = postPermissions(type);
  if (!userHasPostCap(user?.user, permissions.publish_posts)) {
    values = values.filter((v) => v.value !== PostStatusEnum.Publish);
  }
  if (!userHasPostCap(user?.user, permissions.publish_hidden_post)) {
    values = values.filter(
      (v) => [PostStatusEnum.HiddenBS, PostStatusEnum.Hidden].includes(v.value as PostStatusEnum) === false
    );
  }

  if (!userHasPostCap(user?.user, permissions.delete_posts)) {
    values = values.filter((v) => v.value !== PostStatusEnum.Trash);
  }

  if (!userHasPostCap(user?.user, permissions.edit_posts)) {
    values = values.filter((v) => v.value !== PostStatusEnum.Draft);
  }

  // if the user is not the author of the post
  if (user.id !== opts?.author?.id) {
    // if the user is not the author and does not have the capability to edit others posts
    // the only status that the user can set is trash
    if (!userHasPostCap(user?.user, permissions.edit_others_posts)) {
      values = values.filter((v) => v.value === PostStatusEnum.Trash);
    }
    // but if they can not delete others posts, we want to remove the trash status as well
    if (!userHasPostCap(user?.user, permissions.delete_others_posts)) {
      values = values.filter((v) => v.value !== PostStatusEnum.Trash);
    }
  }

  // force include statuses
  if (opts?.include && opts?.include?.length > 0) {
    opts.include.forEach((status) => {
      if (!values.some((v) => v.value === status)) {
        const found = initValues.find((v) => v.value === status);
        if (found) {
          values.push(found);
        }
      }
    });
  }

  // force exclude statuses
  if (opts?.exclude && opts?.exclude?.length > 0) {
    values = values.filter((v) => opts?.exclude?.includes?.(v.value) === false);
  }

  return values;
};

/**
 * Compare a post status value with a PostStatusEnum
 * We want to make sure the comparison is made in a case-insensitive way
 * @param value
 * @param status
 * @returns boolean
 */
export const getIsStatus = (value: string | null | undefined, status: PostStatusEnum | PostStatusEnum[]): boolean => {
  if (!value) {
    return false;
  }

  if (Array.isArray(status)) {
    return status.some((s) => s.toLowerCase() === value?.toLowerCase());
  }

  return value?.toLowerCase() === status?.toLowerCase();
};

/**
 * Convert a string to a PostStatusEnum
 */
export const toPostStatusEnum = (status: PostStatusEnum | string | null | undefined): PostStatusEnum | null => {
  if (!status) {
    return null;
  }

  for (const s of Object.values(PostStatusEnum)) {
    if (s.toLowerCase() === status?.toLowerCase()) {
      return s;
    }
  }

  return null;
};

export const getStatusTypeObject = (
  settings: StatusSettings | DeepNullable<DeepPartial<Frags_Status_Settings_Fragment$data>> | null | undefined
): AddAsType | VisibilityType | (typeof STATUS_VALUES)[PostStatusEnum.Trash] | null => {
  if (!settings) {
    return null;
  }

  const groups = getStatusGroupsFromSettings(settings as StatusSettings);
  if (!groups) {
    if (settings?.status === PostStatusEnum.Trash) {
      return STATUS_VALUES[PostStatusEnum.Trash];
    }

    return null;
  }

  // when approved -> we use the status type object from the visibility
  if (groups.addAs.value === 'add_as_approved' && groups.visibility) {
    return groups.visibility;
  }

  return groups.addAs;
};

/**
 * useStatus hook
 */

export type UseStatusProps<T extends keyof NodeTypes> = {
  type: T;
  statusOptions?: GetStatusOptions;
};

/**
 * Based on the post type and the current user, we want to get the allowed statuses that the user can set
 */
export const useStatusesAllowed = <T extends keyof NodeTypes>(type: T, options?: GetStatusOptions): StatusObject[] => {
  const user = useAuthStore((state) => state?.user);

  const allowedStatuses = useMemo(() => {
    return getAllowedStatusArray(type, user, options);
  }, [user, type, options]);

  return allowedStatuses;
};

// permissions section

/**
 * Get post permissions based on the post type
 */
export type PostCaps = ReturnType<typeof postPermissions>[keyof ReturnType<typeof postPermissions>];

export const postPermissions = <T extends keyof NodeTypes>(type: T) => {
  const { capSingleKey, capPluralKey } = nodeTypes[type] as NodeTypes[T];

  return {
    // singular
    read_post: `read_${capSingleKey}`,
    edit_post: `edit_${capSingleKey}`,
    delete_post: `delete_${capSingleKey}`,
    publish_hidden_post: `publish_hidden_${capSingleKey}`,
    create_post: `create_${capSingleKey}`,

    // plural
    edit_posts: `edit_${capPluralKey}`,
    edit_others_posts: `edit_others_${capPluralKey}`,
    delete_posts: `delete_${capPluralKey}`,
    publish_posts: `publish_${capPluralKey}`,
    read_private_posts: `read_private_${capPluralKey}`,

    // primitive
    read: 'read',
    delete_private_posts: `delete_private_${capPluralKey}`,
    delete_published_posts: `delete_published_${capPluralKey}`,
    delete_others_posts: `delete_others_${capPluralKey}`,
    edit_private_posts: `edit_private_${capPluralKey}`,
    edit_published_posts: `edit_published_${capPluralKey}`,
    create_posts: `edit_${capPluralKey}`,
  } as const;
};

export const userHasPostCap = <T extends keyof NodeTypes>(
  user: ReturnType<(typeof useAuthStore)['getState']>['user']['user'],
  cap: ValueOf<ReturnType<typeof postPermissions<T>>>
) => {
  return user?.capabilities?.includes(cap);
};
